// GetLiveCompanion — persistent cross-app "how do I get fully live" hub.
// Collapsed launcher (bottom-right) + slide-up panel tracking the three things
// between the user and fully-live: company profile, A2P registration, and
// launching each campaign. All state derived from real data via useGetLiveStatus.
//
// Recreated from design_handoff_get_live_companion/prototype-companion.jsx.
// Deviation flagged in the handoff: real A2P registration is a heavy multi-step
// flow that already exists at /settings/a2p-compliance, so "Start registration"
// routes there instead of re-implementing create-a2p-brand in a modal.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, Check, ChevronDown, Building2, Megaphone, CalendarClock, AlertTriangle, BookOpen, Users } from 'lucide-react';
import { useGetLive } from './GetLiveContext';
import { useAuth } from '../../context/AuthContext';
import { a2pBannerContent } from '../../lib/a2pStage';
import SurfRing, { SurfAvatar } from './SurfRing';
import supabase from '../../lib/supabaseClient';

// Settings sections are components on the single /settings page (not their own
// routes); ?section=<id> auto-opens the card (see Settings.jsx deep-link effect).
// Each route also carries ?howto=<key> so the global HowToVideoModal pops the
// matching walkthrough over the destination page (see howToVideos.js).
const A2P_ROUTE = '/settings?section=a2p&howto=a2p';
const COMPANY_ROUTE = '/settings?section=company&howto=company';
const AVAILABILITY_ROUTE = '/calendar-settings?howto=availability';
// Add Lead lives as a modal on the dashboard; ?add=leads auto-opens it on Bulk import.
const LEADS_ROUTE = '/dashboard?add=leads&howto=leads';
const campaignRoute = (id) => `/campaign-management?campaign=${id}&howto=campaign`;

/* ----------------------------------------------------------------- Launcher */
function Launcher({ leftCount, a2pActionable, days, onClick }) {
  const allSet = leftCount === 0;
  // The warning dot + urgency only mean something when A2P actually needs the user.
  // Pending carrier review (the norm now that A2P is captured in onboarding) stays calm.
  const urgentDot = a2pActionable && days <= 5;
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 h-[60px] pl-3 pr-[18px] bg-white rounded-full border border-surfox-line shadow-[0_12px_30px_-8px_rgba(15,23,42,0.28),0_4px_10px_rgba(15,23,42,0.06)] transition-transform hover:-translate-y-0.5"
      aria-label="Open the get-live companion"
    >
      <div className="relative">
        <SurfRing size={44} pct={allSet ? 1 : 0.55}><SurfAvatar size={30} radius={9} /></SurfRing>
        {a2pActionable && (
          <span className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${urgentDot ? 'bg-surfox-red' : 'bg-surfox-amber'}`} />
        )}
      </div>
      <div className="text-left">
        <div className="text-[10.5px] font-extrabold text-surfox-cyanText tracking-[1px] uppercase">Go live</div>
        <div className="text-[13.5px] font-bold text-surfox-ink mt-px">
          {allSet ? 'You’re all set 🎉' : `${leftCount} thing${leftCount === 1 ? '' : 's'} left`}
        </div>
      </div>
      <span className="ml-1.5 w-[26px] h-[26px] rounded-full bg-[#f1f5f9] flex items-center justify-center">
        <ChevronDown size={15} className="text-surfox-muted rotate-180" />
      </span>
    </button>
  );
}

/* ----------------------------------------------------------------- A2P card */
// Stage-aware, mirroring the top A2PTrialBanner. `content` = a2pBannerContent(stage):
//   null            → complete (on their own number)
//   tone 'pending'  → submitted, under carrier review — calm, NO action, NO countdown
//   tone 'action'   → user must register / create campaign — amber, countdown, button
//   tone 'failed'   → rejected — red, "Review" button
function A2PCard({ content, days, onStart }) {
  // Complete — approved, on their own number.
  if (!content) {
    return (
      <div className="mx-3.5 mt-3 mb-1 flex items-center gap-2.5 px-3.5 py-3 rounded-[14px] bg-surfox-greenSoft border border-[#bbf7d0]">
        <span className="flex w-[30px] h-[30px] rounded-[9px] bg-white border border-[#bbf7d0] items-center justify-center">
          <Check size={16} className="text-surfox-green" strokeWidth={3} />
        </span>
        <div className="flex-1">
          <div className="text-[13.5px] font-extrabold text-[#15803d]">You’re on your own A2P number</div>
          <div className="text-[11.5px] text-[#15803d]/85 mt-px">Registration approved — off the shared account.</div>
        </div>
      </div>
    );
  }

  // Pending — submitted during onboarding, awaiting carriers. Nothing to do.
  if (content.tone === 'pending') {
    return (
      <div className="mx-3.5 mt-3 mb-1 flex items-start gap-2.5 px-3.5 py-3 rounded-[14px] bg-surfox-greenSoft border border-[#bbf7d0]">
        <span className="flex w-[30px] h-[30px] rounded-[9px] bg-white border border-[#bbf7d0] items-center justify-center flex-none">
          <Check size={16} className="text-surfox-green" strokeWidth={3} />
        </span>
        <div className="flex-1">
          <div className="text-[13.5px] font-extrabold text-[#15803d]">{content.title}</div>
          <div className="text-[11.5px] text-[#15803d]/85 mt-px leading-[1.5]">{content.subtitle}</div>
        </div>
      </div>
    );
  }

  // Action / failed — the only states that ask the user to do something.
  const failed = content.tone === 'failed';
  const red = failed || days <= 5;
  const accent = red ? 'text-[#be123c]' : 'text-[#92590a]';
  const IconEl = failed ? AlertTriangle : Shield;
  return (
    <div className={`mx-3.5 mt-3 mb-1 rounded-[14px] overflow-hidden border ${red ? 'border-[#fecdd3] bg-[#fff1f2]' : 'border-[#f6e0b6] bg-[#fef6e9]'}`}>
      <div className="flex items-center gap-2.5 px-3.5 pt-3">
        <span className={`flex w-[30px] h-[30px] rounded-[9px] bg-white border items-center justify-center ${red ? 'border-[#fecdd3]' : 'border-[#f6e0b6]'}`}>
          <IconEl size={16} className={accent} />
        </span>
        <span className={`flex-1 text-[13.5px] font-extrabold ${accent}`}>{content.title}</span>
        {!failed && (
          <span className={`text-[11.5px] font-extrabold text-white px-2.5 py-[3px] rounded-full ${red ? 'bg-surfox-red' : 'bg-surfox-amber'}`}>{days}d left</span>
        )}
      </div>
      <div className={`text-[12px] leading-[1.5] px-3.5 pt-[7px] ${accent}/90`}>
        {content.subtitle}
      </div>
      <button onClick={onStart} className="flex items-center justify-center gap-1.5 m-3 h-9 rounded-[9px] w-[calc(100%-24px)] bg-surfox-ink text-white text-[12.5px] font-bold hover:brightness-125">
        {failed ? 'Review' : 'Start registration'} <ArrowRight size={14} />
      </button>
    </div>
  );
}

/* ----------------------------------------- A2P consent-blocked card */
// Brand approved, but the campaign is HELD because the customer's website is
// missing the SMS consent language carriers verify. Show the exact copy to paste
// + a re-check that releases the hold (the cron fires the campaign on its next run).
function ConsentCopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <div className="text-[11px] text-[#92590a]/70">Loading copy…</div>;
  return (
    <div className="bg-white border border-[#f6e0b6] rounded-lg p-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10.5px] font-bold text-[#92590a] uppercase tracking-wide">{label}</span>
        <button
          onClick={async () => { try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* manual select */ } }}
          className="text-[10.5px] text-[#92590a] underline"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="text-[11px] text-[#475569] leading-snug max-h-24 overflow-auto whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function ConsentBlockedCard({ onResolved }) {
  const [open, setOpen] = useState(false);
  const [copy, setCopy] = useState(null);          // { checkbox, privacy }
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);      // 'ok' | 'still' | 'err'

  const loadCopy = async () => {
    try {
      const { data } = await supabase.functions.invoke('verify-sms-consent', { body: { mode: 'peek' } });
      if (data?.scan) setCopy({ checkbox: data.scan.checkboxCopy, privacy: data.scan.privacyCopy });
    } catch (_) { /* best-effort */ }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !copy) loadCopy();
  };

  const recheck = async () => {
    setChecking(true); setResult(null);
    try {
      const { data } = await supabase.functions.invoke('verify-sms-consent', { body: {} });
      if (data?.ready) { setResult('ok'); onResolved?.(); }
      else {
        setResult('still');
        if (data?.checkboxCopy) setCopy({ checkbox: data.checkboxCopy, privacy: data.privacyCopy });
      }
    } catch (_) { setResult('err'); }
    finally { setChecking(false); }
  };

  if (result === 'ok') {
    return (
      <div className="mx-3.5 mt-3 mb-1 flex items-center gap-2.5 px-3.5 py-3 rounded-[14px] bg-surfox-greenSoft border border-[#bbf7d0]">
        <span className="flex w-[30px] h-[30px] rounded-[9px] bg-white border border-[#bbf7d0] items-center justify-center">
          <Check size={16} className="text-surfox-green" strokeWidth={3} />
        </span>
        <div className="flex-1">
          <div className="text-[13.5px] font-extrabold text-[#15803d]">Consent confirmed</div>
          <div className="text-[11.5px] text-[#15803d]/85 mt-px">We’ll launch your texting campaign shortly.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-3.5 mt-3 mb-1 rounded-[14px] overflow-hidden border border-[#f6e0b6] bg-[#fef6e9]">
      <div className="flex items-center gap-2.5 px-3.5 pt-3">
        <span className="flex w-[30px] h-[30px] rounded-[9px] bg-white border border-[#f6e0b6] items-center justify-center">
          <Shield size={16} className="text-[#92590a]" />
        </span>
        <span className="flex-1 text-[13.5px] font-extrabold text-[#92590a]">Add SMS consent to your website</span>
      </div>
      <div className="text-[12px] leading-[1.5] px-3.5 pt-[7px] text-[#92590a]/90">
        Your registration is approved, but carriers need your site to show how people opt in before we launch texting. Add the copy below, then re-check.
      </div>
      <div className="px-3.5 pt-2">
        <button onClick={toggle} className="text-[12px] font-bold text-[#92590a] underline">
          {open ? 'Hide the copy to paste' : 'Show the copy to paste'}
        </button>
      </div>
      {open && (
        <div className="px-3 pt-2 space-y-2">
          <ConsentCopyRow label="Checkbox for your lead form" value={copy?.checkbox} />
          <ConsentCopyRow label="Privacy policy section" value={copy?.privacy} />
        </div>
      )}
      {result === 'still' && (
        <div className="mx-3 mt-2 text-[11.5px] text-[#92590a] bg-white border border-[#f6e0b6] rounded-lg px-2.5 py-1.5">
          Still not detected. Make sure the copy is live on your public site, then re-check.
        </div>
      )}
      {result === 'err' && (
        <div className="mx-3 mt-2 text-[11.5px] text-[#be123c]">Couldn’t run the check. Try again in a moment.</div>
      )}
      <button
        onClick={recheck}
        disabled={checking}
        className="flex items-center justify-center gap-1.5 m-3 h-9 rounded-[9px] w-[calc(100%-24px)] bg-surfox-ink text-white text-[12.5px] font-bold hover:brightness-125 disabled:opacity-60"
      >
        {checking ? 'Checking your site…' : 'Re-check my website'}
      </button>
    </div>
  );
}

const GroupHead = ({ children, right }) => (
  <div className="flex items-center px-4 pt-3.5 pb-1.5">
    <span className="text-[11px] font-extrabold text-surfox-faint uppercase tracking-[1px]">{children}</span>
    {right != null && <span className="ml-auto text-[11px] font-bold text-surfox-faint">{right}</span>}
  </div>
);

// `interactive` keeps a completed row clickable (global-admin audit) so it can
// still route through and trigger the how-to video. Customers get it disabled.
function TaskRow({ Icon, label, sub, done, onClick, interactive = false }) {
  const clickable = !done || interactive;
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`w-full flex items-center gap-3 px-3.5 py-2.5 transition-colors ${clickable ? 'cursor-pointer hover:bg-surfox-rail' : 'cursor-default'}`}
    >
      <span className={`flex w-[30px] h-[30px] rounded-full flex-none items-center justify-center ${done ? 'bg-surfox-greenSoft' : 'bg-surfox-cyanSoft'}`}>
        {done ? <Check size={15} className="text-surfox-green" strokeWidth={3} /> : <Icon size={15} className="text-surfox-cyan" />}
      </span>
      <span className="flex-1 min-w-0 text-left">
        <span className={`block text-[13.5px] font-semibold ${done ? 'text-surfox-faint' : 'text-surfox-ink'}`}>{label}</span>
        <span className="block text-[11.5px] text-surfox-faint mt-px">{sub}</span>
      </span>
      {done
        ? <span className="text-[11.5px] font-bold text-surfox-green">Done</span>
        : <span className="flex items-center gap-0.5 text-[12.5px] font-bold text-surfox-cyanText">Finish <ArrowRight size={13} /></span>}
    </button>
  );
}

function CampaignRow({ c, onClick, interactive = false }) {
  const left = c.total - c.done;
  const clickable = !c.ai_on || interactive;
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`w-full flex items-center gap-3 px-3.5 py-2.5 transition-colors ${clickable ? 'cursor-pointer hover:bg-surfox-rail' : 'cursor-default'}`}
    >
      {c.ai_on
        ? <span className="flex w-[30px] h-[30px] rounded-full bg-surfox-greenSoft items-center justify-center flex-none"><Megaphone size={15} className="text-surfox-green" /></span>
        : <SurfRing size={30} stroke={3} pct={c.done / c.total}><span className="text-[9.5px] font-extrabold text-surfox-ink">{c.done}/{c.total}</span></SurfRing>}
      <span className="flex-1 min-w-0 text-left">
        <span className="block text-[13.5px] font-semibold text-surfox-ink truncate">{c.name}</span>
        <span className="block text-[11.5px] text-surfox-faint mt-px">
          {c.ai_on ? 'Live — texting now' : c.done === 0 ? 'Not started' : `${left} step${left > 1 ? 's' : ''} to launch`}
        </span>
      </span>
      {c.ai_on
        ? <span className="flex items-center gap-1.5 text-[12px] font-semibold text-surfox-green"><span className="w-1.5 h-1.5 rounded-full bg-surfox-green" /> Live</span>
        : <span className="flex items-center gap-0.5 text-[12.5px] font-bold text-surfox-cyanText">{c.done === 0 ? 'Set up' : 'Finish'} <ArrowRight size={13} /></span>}
    </button>
  );
}

/* ----------------------------------------------------------------- Panel */
function Panel({ status, isGlobalAdmin, onClose, onA2P, onCompany, onKnowledge, onLeads, onAvailability, onCampaign }) {
  const { company, knowledge, leads, availability, a2p, trial, campaign } = status;
  const setupLeft = (company.isComplete ? 0 : 1) + (knowledge.isComplete ? 0 : 1)
    + (leads.isComplete ? 0 : 1) + (availability.isComplete ? 0 : 1);
  const campLeft = campaign && !campaign.ai_on ? 1 : 0;
  const stepsLeft = setupLeft + campLeft;
  const allSet = stepsLeft === 0 && a2p.isComplete;
  // Everything the user can act on is done; A2P is just finishing carrier review.
  const nothingActionable = stepsLeft === 0 && !a2p.actionable && !a2p.consentBlocked;
  return (
    <div className="w-[392px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-96px)] bg-white rounded-[20px] overflow-hidden flex flex-col border border-surfox-line shadow-[0_26px_60px_-14px_rgba(15,23,42,0.4),0_6px_16px_rgba(15,23,42,0.1)] motion-safe:animate-sfPanelIn">
      <div className="px-[18px] pt-[17px] pb-4 border-b border-surfox-line2 flex-none">
        <div className="flex items-center gap-3">
          <SurfAvatar size={40} radius={12} ring />
          <div className="flex-1">
            <div className="text-[10.5px] font-extrabold text-surfox-cyanText tracking-[1.3px] uppercase">Surf · Go live</div>
            <div className="text-[16.5px] font-extrabold text-surfox-ink tracking-[-0.3px] mt-0.5">
              {allSet ? 'You’re fully live 🎉' : nothingActionable ? 'You’re all set 🎉' : 'Let’s finish getting you live'}
            </div>
          </div>
          <button onClick={onClose} className="flex p-1 text-surfox-faint hover:text-surfox-muted" aria-label="Collapse">
            <ChevronDown size={18} />
          </button>
        </div>
      </div>

      <div className="overflow-auto flex-1">
        {a2p.consentBlocked
          ? <ConsentBlockedCard onResolved={status.refetch} />
          : <A2PCard content={a2pBannerContent(a2p.stage)} days={trial.daysLeft} onStart={onA2P} />}

        <GroupHead right={`${setupLeft} left`}>Set up your AI</GroupHead>
        <TaskRow Icon={Building2} label="Company profile" sub="Who your AI represents" done={company.isComplete} onClick={onCompany} interactive={isGlobalAdmin} />
        <TaskRow Icon={BookOpen} label="Knowledge base" sub="Upload your filled-in template" done={knowledge.isComplete} onClick={onKnowledge} interactive={isGlobalAdmin} />
        <TaskRow Icon={Users} label="Add your leads" sub="Bulk import your contacts" done={leads.isComplete} onClick={onLeads} interactive={isGlobalAdmin} />
        <TaskRow Icon={CalendarClock} label="Availability" sub="Confirm when your AI can book" done={availability.isComplete} onClick={onAvailability} interactive={isGlobalAdmin} />

        <div className="border-t border-surfox-line2 mt-1.5" />
        <GroupHead right={campaign ? (campaign.ai_on ? 'Live' : '1 to launch') : ''}>Launch your campaign</GroupHead>
        {!campaign
          ? <div className="px-4 py-3 text-[12.5px] text-surfox-faint">No campaign yet.</div>
          : <CampaignRow c={campaign} onClick={() => onCampaign(campaign.id)} interactive={isGlobalAdmin} />}
      </div>

      <div className="flex items-center gap-2.5 px-4 py-3 bg-surfox-rail border-t border-surfox-line flex-none">
        <SurfAvatar size={26} radius={7} />
        <span className="text-[12px] text-surfox-muted leading-[1.4]">
          <b className="text-[#334155]">Surf:</b>{' '}
          {allSet ? 'Nice — everything’s live and on your own number.'
            : nothingActionable ? 'You’re all set. A2P is just finishing carrier review — nothing to do.'
            : a2p.consentBlocked ? 'Add the SMS consent copy to your site and I’ll launch your texting.'
            : a2p.actionable ? 'Knock out A2P first — it’s the only thing on a clock.'
            : 'Work through the steps above, then flip your campaign on whenever you’re ready.'}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- A2P reminder pill */
// After the first campaign goes live, the activation hub collapses to this A2P pill.
// Stage-aware: while the registration is under carrier review (the norm now that A2P
// is captured in Surf onboarding) it's a calm status, NOT a call to action. It only
// turns into a register/fix prompt when the user actually has to do something.
// `content` = a2pBannerContent(stage). The trial countdown only appears while trialing.
function A2PReminderLauncher({ content, days, isTrialing, onClick }) {
  const actionable = !!content && (content.tone === 'action' || content.tone === 'failed');
  const failed = content?.tone === 'failed';

  // Pending review — calm, informational, no urgency.
  if (!actionable) {
    return (
      <button
        onClick={onClick}
        className="group flex items-center gap-3 h-[60px] pl-3 pr-[18px] bg-white rounded-full border border-surfox-line shadow-[0_12px_30px_-8px_rgba(15,23,42,0.28),0_4px_10px_rgba(15,23,42,0.06)] transition-transform hover:-translate-y-0.5"
        aria-label="A2P registration status"
      >
        <span className="flex w-11 h-11 rounded-full items-center justify-center bg-surfox-cyanSoft">
          <Shield size={20} className="text-surfox-cyan" />
        </span>
        <div className="text-left">
          <div className="text-[10.5px] font-extrabold tracking-[1px] uppercase text-surfox-cyanText">A2P registration</div>
          <div className="text-[13.5px] font-bold text-surfox-ink mt-px">Under carrier review</div>
        </div>
        <span className="ml-1.5 w-[26px] h-[26px] rounded-full bg-[#f1f5f9] flex items-center justify-center">
          <ArrowRight size={15} className="text-surfox-muted" />
        </span>
      </button>
    );
  }

  const red = failed || (isTrialing && days <= 5);
  const accent = red ? 'text-surfox-red' : 'text-surfox-amber';
  const IconEl = failed ? AlertTriangle : Shield;
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 h-[60px] pl-3 pr-[18px] bg-white rounded-full border border-surfox-line shadow-[0_12px_30px_-8px_rgba(15,23,42,0.28),0_4px_10px_rgba(15,23,42,0.06)] transition-transform hover:-translate-y-0.5"
      aria-label={failed ? 'Review your A2P registration' : 'Register your A2P number'}
    >
      <span className={`flex w-11 h-11 rounded-full items-center justify-center ${red ? 'bg-surfox-redSoft' : 'bg-surfox-amberSoft'}`}>
        <IconEl size={20} className={accent} />
      </span>
      <div className="text-left">
        <div className={`text-[10.5px] font-extrabold tracking-[1px] uppercase ${accent}`}>
          {failed ? 'A2P · needs attention' : isTrialing ? `A2P · ${days}d left` : 'A2P registration'}
        </div>
        <div className="text-[13.5px] font-bold text-surfox-ink mt-px">
          {failed ? 'Review your registration' : 'Register your A2P number'}
        </div>
      </div>
      <span className="ml-1.5 w-[26px] h-[26px] rounded-full bg-[#f1f5f9] flex items-center justify-center">
        <ArrowRight size={15} className="text-surfox-muted" />
      </span>
    </button>
  );
}

/* ----------------------------------------------------------------- Root */
export default function GetLiveCompanion() {
  const navigate = useNavigate();
  const status = useGetLive();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!status || status.loading) return null;

  // Global admins always get the full 5-step hub, whatever state the tenant is
  // in, so they can audit every step. Customers get the collapse/hide behavior.
  const isGlobalAdmin = user?.role === 'global_admin' || user?.user_metadata?.role === 'global_admin';
  // The hub exists to teach ONE thing: get a campaign live and sending. Once the
  // tenant has done that it retires for good, so it keys off hasEverLaunched
  // (write-once first_activated_at), NOT ai_on / leftCount. Both of those go
  // backwards — pausing a campaign or drafting a new one would resurrect the
  // activation hub months later, nagging a seasoned tenant to "go live" over a
  // draft they may never launch. Drafting campaign #2 is routine work, not
  // activation. A2P is the one thing that outlives retirement (separate
  // compliance clock), so it collapses to the focused reminder below.
  const a2pNeedsUser = status.a2p.actionable || status.a2p.consentBlocked;
  if (!isGlobalAdmin && status.hasEverLaunched && !a2pNeedsUser) return null;

  const go = (route) => { setOpen(false); navigate(route); };
  // Knowledge lives on a tab inside AI Studio (no route of its own). Navigate there,
  // then ask AI Studio to switch to the Knowledge tab (same event the help center uses).
  const goKnowledge = () => {
    setOpen(false);
    navigate('/ai-studio?howto=knowledge');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tour-switch-tab', { detail: { tabId: 'knowledge-base' } }));
    }, 300);
  };

  // Sits above the Crisp support FAB (fixed bottom-6 right-6) so they don't overlap.
  // Post-first-launch the hub is retired; only an A2P item that actually needs the
  // user survives, as this focused reminder (customers only). Reached only when
  // a2pNeedsUser — the calm "under carrier review" state returns null above.
  if (!isGlobalAdmin && status.hasEverLaunched) {
    return (
      <div className="fixed right-6 bottom-[88px] z-[120]">
        <A2PReminderLauncher
          content={a2pBannerContent(status.a2p.stage)}
          days={status.trial.daysLeft}
          isTrialing={status.trial.isTrialing}
          onClick={() => go(A2P_ROUTE)}
        />
      </div>
    );
  }

  // Pre-launch: the full Get-Live hub (launcher + slide-up panel).
  // z-[120] keeps the slide-up panel above in-page sticky headers (e.g. the
  // control room command bar at z-index:100) so its close button is never
  // buried, while staying below full-screen modals (z-9990+) and Surf (z-10000).
  return (
    <div className="fixed right-6 bottom-[88px] z-[120]">
      {open ? (
        <Panel
          status={status}
          isGlobalAdmin={isGlobalAdmin}
          onClose={() => setOpen(false)}
          onA2P={() => go(A2P_ROUTE)}
          onCompany={() => go(COMPANY_ROUTE)}
          onKnowledge={goKnowledge}
          onLeads={() => go(LEADS_ROUTE)}
          onAvailability={() => go(AVAILABILITY_ROUTE)}
          onCampaign={(id) => go(campaignRoute(id))}
        />
      ) : (
        <Launcher
          leftCount={status.leftCount}
          a2pActionable={status.a2p.actionable}
          days={status.trial.daysLeft}
          onClick={() => setOpen(true)}
        />
      )}
    </div>
  );
}
